/**
 * Smart Swirl Card Component
 * Phase 5: Displays a single Smart Swirl with AI color-themed gradient
 */

import React, { useState, useEffect, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { harmonizeColorPalette, generateGradientCSS } from '../../utils/visualHarmony';
import { logUserInteraction } from '../../services/behaviorTracker';
import { useAuth } from '../../contexts/AuthContext';

const SmartSwirlCard = memo(function SmartSwirlCard({ swirl, onClick, isActive = false, onModalOpen }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [thumbnails, setThumbnails] = useState([]);
  const [previewItems, setPreviewItems] = useState([]);
  const [gradientPosition, setGradientPosition] = useState(0);

  // Parse and harmonize color palette
  const rawColorPalette = swirl.color_palette || {
    primary: '#A78BFA',
    gradient: ['#A78BFA', '#C084FC']
  };
  
  const colorPalette = harmonizeColorPalette(rawColorPalette);
  const gradientColors = Array.isArray(colorPalette.gradient) && colorPalette.gradient.length >= 2
    ? colorPalette.gradient
    : [colorPalette.primary || '#A78BFA', '#C084FC'];

  const primaryColor = colorPalette.primary || gradientColors[0];
  const itemCount = Array.isArray(swirl.item_ids) ? swirl.item_ids.length : 0;
  
  // Animate gradient flow on hover
  useEffect(() => {
    if (isHovered) {
      const interval = setInterval(() => {
        setGradientPosition(prev => (prev + 1) % 100);
      }, 50);
      return () => clearInterval(interval);
    }
  }, [isHovered]);
  
  // Darken border for light themes to maintain contrast
  const borderColor = isActive 
    ? (() => {
        // Simple darkening: mix primary with black (10%)
        const rgb = hexToRgb(primaryColor);
        const darkened = {
          r: Math.max(0, rgb.r - 25),
          g: Math.max(0, rgb.g - 25),
          b: Math.max(0, rgb.b - 25)
        };
        return `rgb(${darkened.r}, ${darkened.g}, ${darkened.b})`;
      })()
    : 'transparent';

  // Initialize living canvas on mount (Phase 6.7)
  useEffect(() => {
    if (cardRef.current) {
      cardRef.current.setAttribute('data-smart-swirl-id', swirl.id);
      cardRef.current.classList.add('smart-swirl-card', 'living-canvas-node');
      
      // Initialize living canvas after a short delay to ensure all cards are mounted
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          import('../../lib/livingCanvas.js').then(({ initLivingCanvas }) => {
            initLivingCanvas();
          }).catch(() => {
            // Fail silently
          });
        }
      }, 100);
    }
  }, [swirl.id]);

  // Helper to convert hex to RGB
  function hexToRgb(hex) {
    const cleanHex = hex.replace('#', '');
    return {
      r: parseInt(cleanHex.substring(0, 2), 16),
      g: parseInt(cleanHex.substring(2, 4), 16),
      b: parseInt(cleanHex.substring(4, 6), 16)
    };
  }

  // Fetch preview items (first 3)
  useEffect(() => {
    const loadPreviewItems = async () => {
      if (!swirl.item_ids || !Array.isArray(swirl.item_ids) || swirl.item_ids.length === 0) return;

      // Filter out null/undefined IDs
      const validIds = swirl.item_ids.filter(id => id != null).slice(0, 3);
      if (validIds.length === 0) return;

      try {
        // Try deepsearch.items first, fallback to instagram_content
        let items = null;
        
        try {
          const { data: deepsearchItems, error: deepsearchError } = await supabase
            .schema('deepsearch').from('items')
            .select('id, title, url, source_type, meta, thumbnail_url, auto_tags')
            .in('id', validIds);
          
          if (!deepsearchError && deepsearchItems) {
            items = deepsearchItems;
          }
        } catch (e) {
          // deepsearch.items doesn't exist, try instagram_content
          const { data: instagramData } = await supabase
            .schema('public')
            .from('instagram_content')
            .select('id, title, url, content_type, channel, thumbnail_url, analysis_data, transcription, file_info')
            .in('id', validIds);
          
          if (instagramData) {
            // Map instagram_content to expected format
            items = instagramData.map(item => ({
              id: item.id,
              title: item.title || 'Untitled',
              url: item.url,
              source_type: item.content_type || 'link',
              meta: { thumbnail_url: item.thumbnail_url },
              thumbnail_url: item.thumbnail_url,
              auto_tags: item.analysis_data?.tags ? { topics: item.analysis_data.tags } : 
                        (item.tags ? { topics: Array.isArray(item.tags) ? item.tags : [] } : { topics: [] })
            }));
          }
        }

        if (items) {
          setPreviewItems(items);
          
          // Extract thumbnails
          const thumbUrls = items.map(item => {
            if (item.thumbnail_url) return item.thumbnail_url;
            if (item.source_type === 'image' && item.url) return item.url;
            if (item.meta?.thumbnail_url) return item.meta.thumbnail_url;
            if (item.meta?.image_url) return item.meta.image_url;
            return null;
          }).filter(Boolean);

          setThumbnails(thumbUrls.slice(0, 3));
        }
      } catch (error) {
        console.error('Error loading preview items:', error);
      }
    };

    loadPreviewItems();
  }, [swirl.item_ids]);
  
  // Extract topic theme for tooltip
  const getTooltipText = () => {
    if (previewItems.length > 0) {
      const topics = previewItems
        .flatMap(item => item.auto_tags?.topics || [])
        .filter(Boolean);
      if (topics.length > 0) {
        const mostCommon = topics[0];
        return `Discovered from your recent ${mostCommon} dives.`;
      }
    }
    return 'Discovered from your recent exploration.';
  };
  
  const handleClick = () => {
    // Phase 9.5.2b: Log view interaction for behavioral learning
    if (user?.id && swirl.id) {
      // Log view interaction (swirl is the "item" being viewed)
      logUserInteraction(user.id, swirl.id, 'view', swirl.id).catch(() => {
        // Fail silently
      });
    }
    
    // Navigate to insights view (Phase 8.2)
    navigate(`/swirlspace/swirls/${swirl.id}`);
    
    // Still call onClick if provided for backward compatibility
    if (onClick) {
      onClick(swirl);
    }
  };

  // Generate animated gradient background
  const animatedGradient = isHovered
    ? `linear-gradient(${135 + gradientPosition * 0.01}deg, ${gradientColors[0]}, ${gradientColors[1]})`
    : generateGradientCSS(gradientColors, '135deg');

  return (
    <motion.div
      ref={cardRef}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="smart-swirl-card living-canvas-node rounded-2xl p-4 text-white shadow-md cursor-pointer backdrop-blur-[20px] transition-all relative overflow-hidden group"
      style={{
        background: animatedGradient,
        border: `1px solid rgba(255, 255, 255, 0.15)`,
        fontFamily: 'Inter, sans-serif',
      }}
      animate={{
        scale: isHovered ? 1.03 : 1,
        boxShadow: isActive
          ? `0 0 20px ${primaryColor}44`
          : isHovered
          ? '0 4px 20px rgba(0,0,0,0.1)'
          : '0 2px 6px rgba(0,0,0,0.05)',
      }}
      transition={{ duration: 0.25, ease: 'easeInOut' }}
      whileTap={{ scale: 0.98 }}
      layout
    >
      {/* Animated gradient flow on hover */}
      {isHovered && (
        <motion.div
          className="absolute inset-0 opacity-30"
          style={{
            background: `linear-gradient(${gradientPosition * 3.6}deg, transparent, ${primaryColor}40, transparent)`,
          }}
          animate={{
            background: `linear-gradient(${(gradientPosition + 10) * 3.6}deg, transparent, ${primaryColor}40, transparent)`,
          }}
          transition={{ duration: 0.6, repeat: Infinity, ease: 'linear' }}
        />
      )}

      {/* Shimmer effect on hover */}
      {isHovered && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
        />
      )}

      {/* Floating tooltip on hover */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-2 right-2 bg-black/80 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-lg z-20 pointer-events-none whitespace-nowrap"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            View insights for this Swirl →
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10">
        <h3 className="text-lg font-medium mb-1" style={{ 
          color: '#1A1A1A',
          opacity: 0.9,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500
        }}>
          {swirl.title}
        </h3>
        {swirl.description && (
          <p className="text-sm mb-2 line-clamp-2" style={{ 
            color: '#1A1A1A',
            opacity: 0.75,
            fontFamily: 'Inter, sans-serif'
          }}>
            {swirl.description}
          </p>
        )}
        
        {/* Preview Row - 3 items */}
        {previewItems.length > 0 && (
          <div className="flex gap-2 mt-3 mb-2">
            {previewItems.slice(0, 3).map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05, duration: 0.2 }}
                className="flex-1 min-w-0"
              >
                {thumbnails[i] ? (
                  <div className="w-full h-16 rounded-lg overflow-hidden bg-white/10 border border-white/20">
                    <img
                      src={thumbnails[i]}
                      alt={item.title || ''}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-full h-16 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
                    <span className="text-xs opacity-60 truncate w-full px-2 text-center">
                      {item.title?.substring(0, 15) || 'Item'}
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Item count */}
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs opacity-80" style={{ 
            color: '#1A1A1A',
            fontFamily: 'Inter, sans-serif'
          }}>
            {itemCount} items
          </span>
          
          {/* Energy indicator from similarity score */}
          {swirl.similarity_score !== null && swirl.similarity_score !== undefined && (
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-white/60" 
                style={{
                  opacity: Math.max(0.4, swirl.similarity_score)
                }}
              />
              <span className="text-xs opacity-70" style={{ 
                color: '#1A1A1A',
                fontFamily: 'Inter, sans-serif'
              }}>
                {Math.round(swirl.similarity_score * 100)}% match
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
});

export default SmartSwirlCard;

